/**
 * Brand resolution — single source of truth model.
 *
 * Each tenant's brand lives in ONE place: `tenant.tenant_settings.brand_overrides`
 * (JSONB). The frontend merges that JSONB on top of `defaultBrand` (a bare
 * neutral starter) to produce the active `BrandConfig`.
 *
 * There is NO "named brand registry" — `m2` and `brij` are starter PRESETS
 * the White-Label Editor offers, not runtime objects tenants depend on. See
 * `docs/WHITE_LABELING.md` for the full model.
 *
 * Switching brand at runtime:
 *   import { setActiveBrand } from '@/brand';
 *   setActiveBrand(myTenantOverrides);   // BrandOverrides JSONB or null
 */

import type { BrandConfig, BrandOverrides } from './types';
import { defaultBrand } from './default';
import { presets, listPresetIds } from './presets';
import { mergeBrandOverrides } from './merge-overrides';

// ─── Presets — STARTER TEMPLATES, not a runtime registry ────────────────────
//
// Kept in `./presets` (React-free) so the backend can compile the same file
// into `config/brand_presets.json` — see `scripts/brand/build-presets-manifest.mjs`.
export { presets, listPresetIds, getPreset, presetAsOverrides, combinePresetAndOverrides } from './presets';
export type { PresetId } from './presets';

// ─── Active brand — mutable singleton ───────────────────────────────────────

let _activeOverrides: BrandOverrides | null = null;
let _activeCompanyTenantCode: string | null = null;

export const brand: BrandConfig = { ...defaultBrand };

const _listeners = new Set<() => void>();

export function getActiveOverrides(): BrandOverrides | null {
  return _activeOverrides;
}

export function getActiveCompanyTenantCode(): string | null {
  return _activeCompanyTenantCode;
}

export function setActiveCompanyTenantCode(code: string | null): void {
  _activeCompanyTenantCode = code;
}

/**
 * Activate a brand from a tenant's overrides JSONB (deep-merged onto
 * `defaultBrand`). Pass `null` to revert to defaults.
 *
 * Default-fallback policy: anything the tenant didn't customize
 * inherits from `defaultBrand` (so a tenant that hasn't visited the
 * white-label editor still has a working logo, fonts, colors). What
 * the tenant DID customize must persist — that's the job of the
 * brand-resolution layer above (BrandResolver's cache-first
 * application) and the editor's full-state PATCH.
 *
 * Mutates the singleton `brand` in place so existing `brand.X`
 * references stay valid; subscribers are notified.
 */
export function setActiveBrand(overrides: BrandOverrides | null): void {
  // Bail when the incoming overrides are equivalent to what's already
  // active. App.tsx invokes this up to 3x per mount (sync resolve → host
  // refresh → post-auth user) and consumers (Sidebar, BrandAssets, …) all
  // re-render on every notify; without this guard each mount triggers 3x
  // re-render of the whole brand-aware tree for no visible change.
  const prevSerialized = JSON.stringify(_activeOverrides);
  const nextSerialized = JSON.stringify(overrides);
  if (prevSerialized === nextSerialized) return;

  const next = mergeBrandOverrides(defaultBrand, overrides);
  _activeOverrides = overrides;

  const target = brand as unknown as Record<string, unknown>;
  for (const k of Object.keys(target)) {
    if (!(k in next)) delete target[k];
  }
  Object.assign(brand, next);
  _listeners.forEach(cb => cb());
}

export function subscribeBrandChange(cb: () => void): () => void {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

// ─── Re-exports ─────────────────────────────────────────────────────────────

export { defaultBrand };
export { useBrand } from './useBrand';
export { useBrandAssets } from './useBrandAssets';
export { BrandScope, useBrandScope } from './BrandScope';
export { getAgentCoin, SOLID_AGENT_COLORS } from './coins';

// ─── Deprecated — kept for backward compat during migration ─────────────────

/**
 * @deprecated Use `presets` instead. The old "named brand" model is gone —
 * `getBrand(id)` is now an alias for `getPreset(id) ?? defaultBrand`.
 */
export function getBrand(id?: string | null): BrandConfig {
  if (!id) return defaultBrand;
  return presets[id] ?? defaultBrand;
}

/** @deprecated Replaced by `listPresetIds()`. */
export function listBrandIds(): string[] {
  return listPresetIds();
}

/** @deprecated The active brand id concept is gone. Returns 'default'. */
export function getActiveBrandId(): string {
  return 'default';
}
